import type { BranchKey, WorkspaceKey } from '@festina-life/api'
import { notEmpty, type Optional } from '@festina-life/core'
import { useDebounceCallback } from '@festina-life/ui/hooks'
import { useEffect, useState } from 'react'

import type { ConciseChangesetWithBranchKey } from '../../../../uiModels/changesets/types'
import { useConciseChangesets } from '../../../../uiModels/changesets/useConciseChangesets'
import type { ApprovalStatus } from '../../../configurationItemsTree/types'
import { isConciseAnnulOperation, isConciseEntityOperation } from './helpers'

type Props = {
  query: string
  statusFilter: Record<ApprovalStatus, boolean>
  branchKey: BranchKey
  workspaceKey: Optional<WorkspaceKey>
  shouldIncludeBaseConciseChangesets: boolean
}

export const useFilteredBranchChangesets = ({
  branchKey,
  query,
  statusFilter,
  workspaceKey,
  shouldIncludeBaseConciseChangesets,
}: Props) => {
  const allChangesets = useConciseChangesets({
    branchKey,
    workspaceKey,
    shouldIncludeBaseConciseChangesets,
  })
  const [filteredChangesets, setFilteredChangesets] =
    useState<ConciseChangesetWithBranchKey[]>(allChangesets)

  const debouncedSearch = useDebounceCallback(
    (searchString, status: Record<ApprovalStatus, boolean>) => {
      const needle = searchString.toLowerCase()
      const filteredData = allChangesets
        .map((changeset) => {
          const doesChangesetMatchNeedle = changeset.changesetKey
            ?.toLowerCase()
            .includes(needle)
          const matchingOperations = changeset.operations
            ?.filter(notEmpty)
            .filter((operation) => {
              const matchesStatus = operation.approvalTime
                ? status.approved
                : status.pending

              if (
                isConciseEntityOperation(operation) ||
                isConciseAnnulOperation(operation)
              ) {
                const matchesQuery = isConciseAnnulOperation(operation)
                  ? (operation.name ?? '').toLowerCase().includes(needle)
                  : (operation.entityName ?? '').toLowerCase().includes(needle)

                return (
                  (matchesQuery || doesChangesetMatchNeedle) && matchesStatus
                )
              }

              return !needle && matchesStatus
            })

          return matchingOperations?.length
            ? { ...changeset, operations: matchingOperations }
            : null
        }, [])
        .filter(notEmpty)

      setFilteredChangesets(filteredData)
    },
    300,
    [allChangesets],
  )

  useEffect(() => {
    debouncedSearch(query, statusFilter)
  }, [debouncedSearch, query, statusFilter, allChangesets])

  return filteredChangesets
}
